import { useState, useCallback } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FocusSection } from '@/components/project/focus-section';
import { QueueSection } from '@/components/project/queue-section';
import { ProjectCreateModal } from '@/components/project/project-create-modal';
import {
  useProjects,
  useCreateProject,
  useUpdateProject,
  useReorderProject,
  useDeleteProject,
  type ProjectItem,
} from '@/hooks/use-projects';
import { useWorkspaces } from '@/hooks/use-workspaces';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useEffect } from 'react';
import type { CreateProjectInput } from '@triankl/shared';

export const Route = createFileRoute(
  '/_authenticated/workspace/$workspaceId/projects/'
)({
  component: ProjectsPage,
});

function ProjectsPage() {
  const { workspaceId } = Route.useParams();
  const { setActiveWorkspace } = useWorkspaceStore();
  const { data: workspaces } = useWorkspaces();
  const { data: allProjects, isLoading } = useProjects(workspaceId);

  const createProject = useCreateProject(workspaceId);
  const updateProject = useUpdateProject(workspaceId);
  const reorderProject = useReorderProject(workspaceId);
  const deleteProject = useDeleteProject(workspaceId);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<ProjectItem | null>(null);

  useEffect(() => {
    if (workspaceId) setActiveWorkspace(workspaceId);
  }, [workspaceId, setActiveWorkspace]);

  const workspace = workspaces?.find((w) => w.id === workspaceId);

  const activeProjects = allProjects?.filter((p) => !p.isParked && !p.archivedAt) ?? [];
  const parkedProjects = allProjects?.filter((p) => p.isParked && !p.archivedAt) ?? [];

  const handleTogglePark = useCallback(
    (projectId: string) => {
      const project = allProjects?.find((p) => p.id === projectId);
      if (!project) return;
      updateProject.mutate({ projectId, is_parked: !project.isParked });
    },
    [allProjects, updateProject]
  );

  const handleArchive = useCallback(
    (projectId: string) => {
      deleteProject.mutate(projectId);
    },
    [deleteProject]
  );

  const handleReorder = useCallback(
    (projectId: string, newRank: string) => {
      reorderProject.mutate({ projectId, rank_order: newRank });
    },
    [reorderProject]
  );

  const handleCreateSubmit = (data: CreateProjectInput) => {
    createProject.mutate(data, {
      onSuccess: () => setCreateModalOpen(false),
    });
  };

  const handleEditSubmit = (data: CreateProjectInput) => {
    if (!editingProject) return;
    updateProject.mutate(
      { projectId: editingProject.id, ...data },
      {
        onSuccess: () => setEditingProject(null),
      }
    );
  };

  if (!workspace && workspaces) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <h1 className="text-2xl font-bold">403 -- Access Denied</h1>
        <p className="mt-2 text-muted-foreground">
          You are not a member of this workspace.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {workspace?.name}
          </h1>
          <p className="text-muted-foreground">Projects</p>
        </div>
        <Button onClick={() => setCreateModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Project
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
      ) : (
        <>
          <FocusSection
            projects={activeProjects}
            workspaceId={workspaceId}
            onTogglePark={handleTogglePark}
            onArchive={handleArchive}
          />

          <QueueSection
            projects={parkedProjects}
            workspaceId={workspaceId}
            onReorder={handleReorder}
            onUnpark={(projectId) => updateProject.mutate({ projectId, is_parked: false })}
            onArchive={handleArchive}
            onEdit={(project) => setEditingProject(project)}
          />
        </>
      )}

      <ProjectCreateModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onSubmit={handleCreateSubmit}
        isSubmitting={createProject.isPending}
      />

      <ProjectCreateModal
        open={!!editingProject}
        onOpenChange={(open) => !open && setEditingProject(null)}
        onSubmit={handleEditSubmit}
        project={editingProject}
        isSubmitting={updateProject.isPending}
      />
    </div>
  );
}
